import React from "react";
import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";
import { StyledButton } from "../../../CommonUI/BuildlingBlocks/StyledButton";
import { UILayer } from "../../../CommonUI/BuildlingBlocks/UILayer";
import { FormConfiguration } from "../../../Form/FormConfiguration";
import { FormBuilder } from "../../../Form/FormBuilder";
import { FormAssignmentType, FormStep } from "../../../rule-engine-core/Flow/Steps/UI/FormStep";
import { PropertyEditorExpression } from "../../../CommonUI/PropertyEditing/PropertyEditorExpression";
import { ExpressionViewEmptyBehavior } from "../../../Expressions/ExpressionView";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { PropertyEditorToggleButtons } from "../../../CommonUI/PropertyEditing/PropertyEditorToggleButtons";
import { Divider } from "@mui/material";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";
import { UIButton } from "../../../CommonUI/BuildlingBlocks/UIButton";
import { FlowBuilder } from "../../FlowBuilder";
import { UISearchControl, UISearchControlItem } from "../../../CommonUI/BuildlingBlocks/UISearchControl";
import { ExpressionConfiguration } from "../../../rule-engine-core/Expressions/ExpressionConfiguration";
import { ExpressionValueType } from "../../../rule-engine-core/Expressions/ExpressionValueType";
import { AppGlueCorePlugins } from "../../../rule-engine-core/AppGlueCorePlugins";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";

interface IFormEditUI {
    form: FormConfiguration;
    name?: string;
    onSave: (data: FormConfiguration) => void;
    onClose: () => void;
    dataDefinition?: DataDefinition;
    onFormNameUpdate: (formName: string) => void;
    stepData: FormStep;
}

export const FormEditUI = ({
    onSave,
    form: propform,
    name,
    onClose,
    dataDefinition,
    onFormNameUpdate,
    stepData,
}: IFormEditUI) => {
    const [open, setOpen] = React.useState<boolean>(false);
    const [openStartFlow, setOpenStartFlow] = React.useState<boolean>(false);
    const [openEndFlow, setOpenEndFlow] = React.useState<boolean>(false);
    const [form, setForm] = React.useState<FormConfiguration>(propform);

    const onFormSave = () => {
        setOpen(false);
        onSave(form);
    };
    const onFormClose = () => {
        setOpen(false);
        setForm(propform);
        onClose();
    };

    return (
        <UISpacedBoxContent>
            <PropertyEditorBoolean
                editObject={stepData}
                propertyName={"embeddedForm"}
                label={"Non Shared Form"}
            />

            <PropertyEditorCollapse
                editObject={stepData}
                collapseLogicOrField={"embeddedForm"}
            >
                <>pick form</>
            </PropertyEditorCollapse>

            <StyledButton
                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                variant="contained"
                onClick={() => setOpen(true)}
            >
                Edit Form
            </StyledButton>

            <PropertyEditorExpression
                label={"Task Name"}
                editObject={stepData}
                propertyName={"taskName"}
                emptyBehavior={ExpressionViewEmptyBehavior.VALUE}
            />
            <PropertyEditorExpression
                label={"Task Description"}
                editObject={stepData}
                propertyName={"taskDescription"}
                emptyBehavior={ExpressionViewEmptyBehavior.VALUE}
            />

            <Divider />

            <PropertyEditorToggleButtons
                label={"Assign Form"}
                editObject={stepData}
                propertyName={"assignForm"}
                options={Object.values(FormAssignmentType)}
            />

            <PropertyEditorCollapse
                editObject={stepData}
                collapseLogicOrField={() => {
                    return stepData.assignForm === FormAssignmentType.ASSIGNED;
                }}
            >
                <UISpacedBoxContent>
                    <PropertyEditorExpression
                        label={"Assignment Channel"}
                        editObject={stepData}
                        propertyName={"channelName"}
                        renderValueEditor={(expression) => {
                            let val =
                                expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VALUE
                                    ? [
                                          new UISearchControlItem<string>(
                                              expression.rootExpression.expressionRoot.value,
                                              expression.rootExpression.expressionRoot.value,
                                          ),
                                      ]
                                    : undefined;

                            return (
                                <UISearchControl<string>
                                    multiSelect={false}
                                    searchTitle={"Pick Channel"}
                                    value={val}
                                    noItemsText={"No Channel Selected"}
                                    searchItems={async (searchText: string): Promise<UISearchControlItem<string>[]> => {
                                        let channels = await AppGlueCorePlugins.AssignmentStorage?.getAllChannels(
                                            AppGlueCorePlugins.UserContext.getCurrentUser(),
                                            false,
                                        );
                                        if (!channels) return [];

                                        if (!searchText || searchText.length === 0)
                                            return channels.map((c) => {
                                                return new UISearchControlItem<string>(c, c);
                                            });

                                        return channels
                                            .filter((c) => c.toLowerCase().includes(searchText.toLowerCase()))
                                            .map((c) => {
                                                return new UISearchControlItem<string>(c, c);
                                            });
                                    }}
                                    onChange={(val: UISearchControlItem<string>[] | undefined): void => {
                                        if (val && val.length !== 0)
                                            expression.rootExpression.expressionRoot.value = val[0].name;
                                        else expression.rootExpression.expressionRoot.value = undefined;

                                        StateManager.propertyChanged(stepData, "channelName");
                                    }}
                                ></UISearchControl>
                            );
                        }}
                        emptyBehavior={ExpressionViewEmptyBehavior.VALUE}
                    />

                    <PropertyEditorBoolean
                        editObject={stepData}
                        propertyName={"subAssignToUser"}
                        label={"Assign Form To User"}
                    />

                    <PropertyEditorCollapse
                        editObject={stepData}
                        collapseLogicOrField={"subAssignToUser"}
                    >
                        <PropertyEditorExpression
                            label={"User"}
                            editObject={stepData}
                            propertyName={"channelMemberAssignment"}
                            renderValueEditor={(expression) => {
                                if (
                                    expression.rootExpression.expressionRoot.valueType === ExpressionValueType.UNSET ||
                                    expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VALUE
                                ) {
                                    let val =
                                        expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VALUE
                                            ? expression.rootExpression.expressionRoot.value.map((i: string) => {
                                                  return new UISearchControlItem<string>(i, i);
                                              })
                                            : undefined;

                                    return (
                                        <UISearchControl<string>
                                            multiSelect={true}
                                            searchTitle={"Pick Channel User"}
                                            value={val}
                                            noItemsText={"No User Selected"}
                                            searchItems={async (
                                                searchText: string,
                                            ): Promise<UISearchControlItem<string>[]> => {
                                                let expression = Reflect.get(
                                                    stepData,
                                                    "channelName",
                                                ) as ExpressionConfiguration;

                                                let d = "ss";
                                                if (d) return [];

                                                console.log("should not get here");

                                                let channel =
                                                    expression.rootExpression.expressionRoot.valueType ===
                                                    ExpressionValueType.VALUE
                                                        ? expression.rootExpression.expressionRoot.value
                                                        : undefined;

                                                let users =
                                                    await AppGlueCorePlugins.AssignmentStorage?.getUsersInChannel(
                                                        AppGlueCorePlugins.UserContext.getCurrentUser(),
                                                        channel,
                                                    );

                                                if (!users) return [];

                                                return users
                                                    .filter((c) => c.toLowerCase().includes(searchText.toLowerCase()))
                                                    .map((u) => {
                                                        return new UISearchControlItem<string>(u, u);
                                                    });
                                            }}
                                            onChange={(val: UISearchControlItem<string>[] | undefined): void => {
                                                if (val && val.length !== 0)
                                                    expression.rootExpression.expressionRoot.value = val[0].name;
                                                else expression.rootExpression.expressionRoot.value = undefined;

                                                StateManager.propertyChanged(stepData, "channelName");
                                            }}
                                        ></UISearchControl>
                                    );
                                }
                                return undefined;
                            }}
                            emptyBehavior={ExpressionViewEmptyBehavior.VALUE}
                        />
                    </PropertyEditorCollapse>
                </UISpacedBoxContent>
            </PropertyEditorCollapse>

            <Divider />

            <PropertyEditorExpression
                label={"Due Date"}
                editObject={stepData}
                propertyName={"dueDate"}
                emptyBehavior={ExpressionViewEmptyBehavior.VALUE}
            />

            <PropertyEditorExpression
                label={"Warn Date"}
                editObject={stepData}
                propertyName={"warnDate"}
                emptyBehavior={ExpressionViewEmptyBehavior.VALUE}
            />

            <Divider />

            <PropertyEditorBoolean
                editObject={stepData}
                propertyName={"expireForm"}
                label={"Expire Form"}
            />

            <PropertyEditorCollapse
                editObject={stepData}
                collapseLogicOrField={"expireForm"}
            >
                <PropertyEditorBoolean
                    editObject={stepData}
                    propertyName={"cancelFlowOnExpire"}
                    label={"Cancel Flow On Expire"}
                />

                <PropertyEditorExpression
                    label={"Expire Form Date"}
                    editObject={stepData}
                    propertyName={"expireFormTime"}
                    emptyBehavior={ExpressionViewEmptyBehavior.EXPRESSION}
                />
            </PropertyEditorCollapse>

            <Divider />

            <UILabel>Event Configuration</UILabel>

            <UIButton
                variant={"link_underlined"}
                onClick={() => setOpenStartFlow(true)}
            >
                Start Flow
            </UIButton>

            <UIButton
                variant={"link_underlined"}
                onClick={() => setOpenEndFlow(true)}
            >
                End Flow
            </UIButton>

            <UILayer
                open={open}
                fullScreen
                padding="0px 0px"
                renderUI={() => (
                    <FormBuilder
                        form={form}
                        formName={name}
                        onFormSave={onFormSave}
                        onFormClose={onFormClose}
                        onFormNameUpdate={onFormNameUpdate}
                        dataDefinition={dataDefinition ? () => dataDefinition : undefined}
                    />
                )}
                onClose={() => setOpen(false)}
            />
            <UILayer
                open={openStartFlow}
                fullScreen
                padding="0px 0px"
                renderUI={() => (
                    <FlowBuilder
                        flow={stepData.startFlow}
                        flowName={"Start Form"}
                        onFlowSave={() => setOpenStartFlow(false)}
                        onFlowCancel={() => setOpenStartFlow(false)}
                    />
                )}
                onClose={() => setOpen(false)}
            />
            <UILayer
                open={openEndFlow}
                fullScreen
                padding="0px 0px"
                renderUI={() => (
                    <FlowBuilder
                        flow={stepData.endFlow}
                        flowName={"End Form"}
                        onFlowSave={() => setOpenEndFlow(false)}
                        onFlowCancel={() => setOpenEndFlow(false)}
                    />
                )}
                onClose={() => setOpen(false)}
            />
        </UISpacedBoxContent>
    );
};
